import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import CategoryIcon from '@mui/icons-material/Category';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
}

interface ImageFolder {
  id: string;
  name: string;
  color?: string;
  imageCount?: number;
  createdAt: string;
  createdBy?: string;
}

interface ImageInfoModalProps {
  open: boolean;
  onClose: () => void;
  image: ImageItem | null;
  folders: ImageFolder[];
}

const ImageInfoModal: React.FC<ImageInfoModalProps> = ({
  open,
  onClose,
  image,
  folders
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  if (!image) return null;

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Klasör adını bul
  const getFolderName = () => {
    if (!image.folderId) return tt('Ana Dizin', 'Main Directory');
    const folder = folders.find(f => f.id === image.folderId);
    return folder ? folder.name : tt('Bilinmeyen Klasör', 'Unknown Folder');
  };

  // Klasör rengini bul
  const getFolderColor = () => {
    if (!image.folderId) return '#2196f3';
    const folder = folders.find(f => f.id === image.folderId);
    return folder?.color || '#2196f3';
  };

  // URL'yi kopyala
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(image.url);
    // Snackbar göstermek için parent'a bildirim gönderebiliriz
    alert(tt("URL kopyalandı!", "URL copied!"));
  };

  // Dosya uzantısını al
  const getFileExtension = () => {
    return image.type || image.name.split('.').pop()?.toUpperCase() || tt('Bilinmiyor', 'Unknown');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon color="primary" />
          <Typography variant="h6">
            {tt('Resim Bilgileri', 'Image Information')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Sol Taraf - Resim Önizleme */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                width: '100%',
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'grey.300'
              }}
            >
              <Box
                component="img"
                src={image.url}
                alt={image.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            
            {/* Thumbnail Bilgisi */}
            {image.thumbnailUrl && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Chip
                  icon={<ImageIcon />}
                  label={tt('Küçük resim mevcut', 'Thumbnail available')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            )}
          </Grid>

          {/* Sağ Taraf - Detaylar */}
          <Grid item xs={12} md={7}>
            {/* Dosya Adı */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ImageIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Dosya Adı', 'File Name')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold">
                {image.name}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Klasör */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {image.folderId ? (
                  <FolderIcon fontSize="small" sx={{ color: getFolderColor() }} />
                ) : (
                  <HomeIcon fontSize="small" color="action" />
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Klasör', 'Folder')}
                </Typography>
              </Box>
              <Chip
                icon={image.folderId ? <FolderIcon /> : <HomeIcon />}
                label={getFolderName()}
                sx={{
                  bgcolor: image.folderId ? `${getFolderColor()}20` : 'grey.100',
                  color: image.folderId ? getFolderColor() : 'text.primary'
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Dosya Özellikleri */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <StorageIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Dosya Özellikleri', 'File Properties')}
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {tt('Boyut', 'Size')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {tt('Tür', 'Type')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {getFileExtension()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Oluşturulma Bilgisi */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Tarih Bilgisi', 'Date Information')}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                {new Date(image.createdAt).toLocaleString(localeCode, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>

            {/* Oluşturan Kişi */}
            {image.createdBy && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      {tt('Yükleyen', 'Uploaded By')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    ID: {image.createdBy}
                  </Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* URL Bilgisi */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LinkIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('URL', 'URL')}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  wordBreak: 'break-all',
                  maxHeight: 80,
                  overflow: 'auto'
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {image.url}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={handleCopyUrl}
                sx={{ mt: 1 }}
              >
                {tt("URL'yi Kopyala", 'Copy URL')}
              </Button>
            </Box>

            {/* ID Bilgisi */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>ID:</strong> {image.id}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageInfoModal;

